import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { AdminProviderHealthRow, AdminProviderHealthWindow } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { WINDOWS, successTone, fmtLatency, topError } from './health-format';

function fmtAgo(iso: string | null): string {
  if (!iso) return '—';
  const mins = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/**
 * Per-provider reliability from provider-attributed invocation metrics:
 * volume, success rate, latency percentiles, last failure, and the dominant
 * error type. Only providers with attributed traffic in the window appear.
 */
const WINDOW_HOURS: Record<AdminProviderHealthWindow, number> = {
  '24h': 24,
  '7d': 168,
  '30d': 720,
};

export function ProviderHealthPanel() {
  const [windowKey, setWindowKey] = useState<AdminProviderHealthWindow>('24h');
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.adminProviders.health(windowKey),
    queryFn: () => api.adminGetProviderHealth(windowKey),
  });

  const providers: AdminProviderHealthRow[] = data?.providers ?? [];

  // Attribution began at deploy time — a window that starts earlier is
  // reporting on partial data and must say so.
  const windowStartMs = Date.now() - WINDOW_HOURS[windowKey] * 3_600_000;
  const partialSince =
    data?.attributionSince && Date.parse(data.attributionSince) > windowStartMs
      ? new Date(data.attributionSince).toISOString().slice(0, 10)
      : null;

  return (
    <div className="rounded-lg border border-border bg-card p-4 mb-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold">Provider reliability</h2>
        <div className="flex gap-1 text-xs">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => setWindowKey(w.key)}
              className={`px-2 py-1 rounded ${windowKey === w.key ? 'bg-primary text-primary-foreground' : 'border border-border'}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Success rate and latency of attributed provider invocations. Free and client-side entity
        calls are excluded.
      </p>
      {partialSince && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mb-3">
          Partial window: provider attribution began {partialSince}, after this window's start —
          rates and percentiles cover only the attributed portion.
        </p>
      )}

      {isError ? (
        <p className="text-sm text-destructive">Failed to load provider health.</p>
      ) : isLoading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : providers.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No attributed provider invocations in this window.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-1.5 pr-4 font-medium">Provider</th>
                <th className="py-1.5 pr-4 font-medium text-right">Calls</th>
                <th className="py-1.5 pr-4 font-medium text-right">Success</th>
                <th className="py-1.5 pr-4 font-medium text-right">p50</th>
                <th className="py-1.5 pr-4 font-medium text-right">p95</th>
                <th className="py-1.5 pr-4 font-medium text-right">Last failure</th>
                <th className="py-1.5 font-medium text-right">Top error</th>
              </tr>
            </thead>
            <tbody>
              {providers.map((p) => (
                <tr key={p.provider} className="border-b border-border last:border-0">
                  <td className="py-1.5 pr-4">{p.provider}</td>
                  <td className="py-1.5 pr-4 text-right tabular-nums">
                    {p.totalInvocations.toLocaleString()}
                  </td>
                  <td
                    className={`py-1.5 pr-4 text-right tabular-nums ${successTone(p.successRate)}`}
                  >
                    {(p.successRate * 100).toFixed(1)}%
                  </td>
                  <td className="py-1.5 pr-4 text-right tabular-nums">
                    {fmtLatency(p.p50LatencyMs)}
                  </td>
                  <td className="py-1.5 pr-4 text-right tabular-nums">
                    {fmtLatency(p.p95LatencyMs)}
                  </td>
                  <td className="py-1.5 pr-4 text-right tabular-nums">{fmtAgo(p.lastFailureAt)}</td>
                  <td className="py-1.5 text-right">{topError(p.errorCounts)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
